/**
 * Conditional transport for the authoritative, card-scoped live-state route.
 *
 * This intentionally does not use `apiJson`: a 304 is success here, whereas
 * the generic JSON helper correctly treats every non-2xx response as an
 * error. Keeping the special HTTP semantics local prevents them leaking into
 * unrelated API calls.
 */
import {
  cardLiveStateSchema,
  type CardLiveState,
} from '@chaos/types';

import { ApiError, apiFetch } from './client';

export interface CardLiveStateCacheEntry {
  payload: CardLiveState;
  /** Strong validator returned by the gateway, including its quotes. */
  etag: string;
  /** Browser time when the complete transport exchange succeeded (200 or 304). */
  transportFreshAt: number;
  /** Latest server observation clock, including a bodyless 304 observation. */
  serverObservedAt?: string;
}

/** Immutable per-card value consumed by React's external-store protocol. */
export interface CardLiveStateTransportSnapshot {
  readonly entry: CardLiveStateCacheEntry | undefined;
  readonly consecutiveErrors: number;
}

interface InFlightCardLiveStateRequest {
  controller: AbortController;
  callers: Set<symbol>;
  cancelled: boolean;
  promise: Promise<CardLiveStateCacheEntry>;
}

export const LIVE_STATE_ROUTE = (cardId: string): string =>
  `/cards/${encodeURIComponent(cardId)}/live-state`;

/** Keep conditional validators useful without retaining every card ever opened. */
export const CARD_LIVE_STATE_ETAG_TTL_MS = 5 * 60_000;
export const CARD_LIVE_STATE_MAX_ENTRIES = 64;
/** Named hard ceiling, shorter than the first 9-second freshness boundary. */
export const CARD_LIVE_STATE_REQUEST_TIMEOUT_MS = 8_000;

function expectedEtag(stateVersion: string): string {
  return `"${stateVersion}"`;
}

const INVALID_LIVE_STATE_OBSERVATION_CLOCK = 'invalid_live_state_observation_clock';

function invalidObservationClock(): ApiError {
  return new ApiError(
    304,
    INVALID_LIVE_STATE_OBSERVATION_CLOCK,
    'Live state returned an invalid observation clock',
  );
}

function isInvalidObservationClock(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    error.status === 304 &&
    error.code === INVALID_LIVE_STATE_OBSERVATION_CLOCK
  );
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Aborted', 'AbortError');
}

/**
 * Race the whole operation, not just `fetch()`. Mocks and some transports may
 * ignore AbortSignal; this still settles the physical request at the deadline.
 */
function rejectOnAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(abortReason(signal));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortReason(signal));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}

async function apiError(response: Response): Promise<ApiError> {
  let body: { error?: string; message?: string } = {};
  try {
    body = (await response.json()) as { error?: string; message?: string };
  } catch {
    // Keep the generic API error wording when an error body is empty.
  }
  return new ApiError(
    response.status,
    body.error ?? `http_${response.status}`,
    body.message ?? response.statusText ?? 'Request failed',
    body,
  );
}

/**
 * Keyed conditional transport. One in-flight request per card means focus,
 * visibility, multiple QueryClients, and React StrictMode can all ask for a
 * refresh without emitting duplicate physical traffic.
 */
export class CardLiveStateTransport {
  /** Bounded HTTP validator cache. The observed last-known value lives in snapshots. */
  private readonly entries = new Map<string, CardLiveStateCacheEntry>();
  private readonly inFlight = new Map<string, InFlightCardLiveStateRequest>();
  /** Per-card physical-request failures; observers must never each count one. */
  private readonly consecutiveErrors = new Map<string, number>();
  private readonly snapshots = new Map<string, CardLiveStateTransportSnapshot>();
  private readonly listeners = new Map<string, Set<() => void>>();

  constructor(
    private readonly maxEntries = CARD_LIVE_STATE_MAX_ENTRIES,
    private readonly etagTtlMs = CARD_LIVE_STATE_ETAG_TTL_MS,
    private readonly requestTimeoutMs = CARD_LIVE_STATE_REQUEST_TIMEOUT_MS,
  ) {}

  /** The bounded conditional cache, exposed for focused transport assertions. */
  get(cardId: string): CardLiveStateCacheEntry | undefined {
    return this.entries.get(cardId);
  }

  getConsecutiveErrors(cardId: string): number {
    return this.consecutiveErrors.get(cardId) ?? 0;
  }

  /** Stable by identity until this card's external-store value changes. */
  getSnapshot(cardId: string): CardLiveStateTransportSnapshot {
    const current = this.snapshots.get(cardId);
    if (current) return current;
    const created = {
      entry: this.entries.get(cardId),
      consecutiveErrors: this.getConsecutiveErrors(cardId),
    } satisfies CardLiveStateTransportSnapshot;
    this.snapshots.set(cardId, created);
    this.trimUnobservedSnapshots();
    return created;
  }

  subscribe(cardId: string, listener: () => void): () => void {
    const cardListeners = this.listeners.get(cardId) ?? new Set<() => void>();
    cardListeners.add(listener);
    this.listeners.set(cardId, cardListeners);
    return () => {
      cardListeners.delete(listener);
      if (cardListeners.size) return;
      this.listeners.delete(cardId);
      // Observer-scoped degradation must not leak into a later mount.
      this.consecutiveErrors.delete(cardId);
      this.snapshots.delete(cardId);
    };
  }

  clear(cardId?: string): void {
    if (cardId !== undefined) {
      this.cancelInFlight(cardId);
      this.entries.delete(cardId);
      this.consecutiveErrors.delete(cardId);
      if (this.listeners.has(cardId)) this.publish(cardId, undefined);
      else this.snapshots.delete(cardId);
      return;
    }

    const affected = new Set([
      ...this.entries.keys(),
      ...this.inFlight.keys(),
      ...this.consecutiveErrors.keys(),
      ...this.snapshots.keys(),
      ...this.listeners.keys(),
    ]);
    for (const key of this.inFlight.keys()) this.cancelInFlight(key);
    this.entries.clear();
    this.consecutiveErrors.clear();
    for (const key of affected) {
      if (this.listeners.has(key)) this.publish(key, undefined);
      else this.snapshots.delete(key);
    }
  }

  private cancelInFlight(cardId: string): void {
    const pending = this.inFlight.get(cardId);
    if (!pending) return;
    pending.cancelled = true;
    this.inFlight.delete(cardId);
    pending.controller.abort(new DOMException('Live-state cache cleared', 'AbortError'));
  }

  fetch(cardId: string, signal?: AbortSignal): Promise<CardLiveStateCacheEntry> {
    if (signal?.aborted) return Promise.reject(abortReason(signal));
    const pending = this.inFlight.get(cardId);
    if (pending) return this.attachCaller(cardId, pending, signal);

    const controller = new AbortController();
    const record: InFlightCardLiveStateRequest = {
      controller,
      callers: new Set(),
      cancelled: false,
      promise: Promise.resolve(undefined as never),
    };
    record.promise = this.request(cardId, controller).then(
      (entry) => {
        // A detached/old generation is never allowed to commit into a newer one.
        if (
          record.cancelled ||
          controller.signal.aborted ||
          this.inFlight.get(cardId) !== record
        ) {
          throw abortReason(controller.signal);
        }
        this.store(cardId, entry);
        this.consecutiveErrors.delete(cardId);
        // A complete physical 200/304 publishes exactly one current-key settle.
        this.publish(cardId, entry);
        return entry;
      },
      (error: unknown) => {
        if (!record.cancelled && this.inFlight.get(cardId) === record) {
          // A malformed/non-advancing 304 is a rejected observation, not a
          // newer observation that may age or repaint the last-known value.
          // Keep the entire external-store snapshot byte-for-byte unchanged.
          if (!isInvalidObservationClock(error)) this.recordError(cardId);
        }
        throw error;
      },
    ).finally(() => {
      // A late old settle cannot delete a newer generation.
      if (this.inFlight.get(cardId) === record) this.inFlight.delete(cardId);
    });
    this.inFlight.set(cardId, record);
    return this.attachCaller(cardId, record, signal);
  }

  private attachCaller(
    cardId: string,
    pending: InFlightCardLiveStateRequest,
    signal?: AbortSignal,
  ): Promise<CardLiveStateCacheEntry> {
    const token = Symbol(cardId);
    pending.callers.add(token);
    return new Promise<CardLiveStateCacheEntry>((resolve, reject) => {
      let settled = false;
      const detach = () => {
        pending.callers.delete(token);
        signal?.removeEventListener('abort', onAbort);
        if (!pending.callers.size && !settled) {
          pending.cancelled = true;
          // Remove first: a synchronous remount must begin a fresh generation.
          if (this.inFlight.get(cardId) === pending) this.inFlight.delete(cardId);
          pending.controller.abort(signal?.reason);
        }
      };
      const onAbort = () => {
        if (settled) return;
        detach();
        settled = true;
        reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
      };
      if (signal?.aborted) {
        onAbort();
        return;
      }
      signal?.addEventListener('abort', onAbort, { once: true });
      pending.promise.then(
        (entry) => {
          if (settled) return;
          settled = true;
          detach();
          resolve(entry);
        },
        (error: unknown) => {
          if (settled) return;
          settled = true;
          detach();
          reject(error);
        },
      );
    });
  }

  private async request(
    cardId: string,
    controller: AbortController,
  ): Promise<CardLiveStateCacheEntry> {
    const previous = this.cached(cardId);
    const { signal } = controller;
    const timeout = setTimeout(
      () => controller.abort(new DOMException('Timed out', 'TimeoutError')),
      this.requestTimeoutMs,
    );
    try {
      return await rejectOnAbort(this.readResponse(cardId, previous, signal), signal);
    } finally {
      clearTimeout(timeout);
    }
  }

  private async readResponse(
    cardId: string,
    previous: CardLiveStateCacheEntry | undefined,
    signal: AbortSignal,
  ): Promise<CardLiveStateCacheEntry> {
    const response = await apiFetch(LIVE_STATE_ROUTE(cardId), {
      headers: previous ? { 'If-None-Match': previous.etag } : undefined,
      signal,
    });

    if (response.status === 304) {
      if (!previous) {
        throw new ApiError(304, 'live_state_without_cache', 'Live state returned 304 without a cache');
      }
      const etag = response.headers.get('ETag');
      if (etag !== expectedEtag(previous.payload.stateVersion)) {
        throw new ApiError(304, 'invalid_live_state_etag', 'Live state returned an invalid ETag');
      }
      const observedAt = response.headers.get('X-Chaos-Live-State-Computed-At');
      const observedAtMs = observedAt === null ? Number.NaN : Date.parse(observedAt);
      const previousObservedAtMs = Date.parse(
        previous.serverObservedAt ?? previous.payload.computedAt,
      );
      const observationClockAdvances = observedAtMs > previousObservedAtMs;
      if (
        observedAt === null ||
        !Number.isFinite(observedAtMs) ||
        !Number.isFinite(previousObservedAtMs) ||
        !observationClockAdvances
      ) {
        throw invalidObservationClock();
      }
      return {
        ...previous,
        etag,
        transportFreshAt: Date.now(),
        serverObservedAt: observedAt,
      };
    }

    if (!response.ok) throw await apiError(response);

    const payload = cardLiveStateSchema.parse(await response.json());
    if (payload.cardId !== cardId) {
      throw new ApiError(200, 'live_state_card_mismatch', 'Live state returned a different card');
    }
    const etag = response.headers.get('ETag');
    if (etag !== expectedEtag(payload.stateVersion)) {
      throw new ApiError(200, 'invalid_live_state_etag', 'Live state returned an invalid ETag');
    }
    const observedAt = response.headers.get('X-Chaos-Live-State-Computed-At');
    return {
      payload,
      etag,
      transportFreshAt: Date.now(),
      serverObservedAt:
        observedAt && Number.isFinite(Date.parse(observedAt))
          ? observedAt
          : payload.computedAt,
    };
  }

  private cached(cardId: string): CardLiveStateCacheEntry | undefined {
    const entry = this.entries.get(cardId);
    if (!entry) return undefined;
    if (Date.now() - entry.transportFreshAt > this.etagTtlMs) {
      this.entries.delete(cardId);
      // Validator expiry must not discard the observer's last server provenance.
      return undefined;
    }
    // Map insertion order is the LRU order: a validator used on this poll is hot.
    this.entries.delete(cardId);
    this.entries.set(cardId, entry);
    return entry;
  }

  private store(cardId: string, entry: CardLiveStateCacheEntry): void {
    this.entries.delete(cardId);
    this.entries.set(cardId, entry);
    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next().value as string | undefined;
      if (oldest === undefined) return;
      this.entries.delete(oldest);
      // The validator is gone, but an observed last-known payload remains valid
      // provenance. Publishing makes eviction visible without erasing that fact.
      if (this.listeners.has(oldest)) {
        this.publish(oldest, this.snapshots.get(oldest)?.entry);
      } else {
        this.snapshots.delete(oldest);
        this.consecutiveErrors.delete(oldest);
      }
    }
  }

  private recordError(cardId: string): void {
    const next = this.getConsecutiveErrors(cardId) + 1;
    this.consecutiveErrors.delete(cardId);
    this.consecutiveErrors.set(cardId, next);
    this.publish(cardId, this.snapshots.get(cardId)?.entry ?? this.entries.get(cardId));
    while (this.consecutiveErrors.size > this.maxEntries) {
      const oldest = this.consecutiveErrors.keys().next().value as string | undefined;
      if (oldest === undefined) return;
      this.consecutiveErrors.delete(oldest);
      if (this.listeners.has(oldest)) {
        this.publish(oldest, this.snapshots.get(oldest)?.entry ?? this.entries.get(oldest));
      } else {
        this.snapshots.delete(oldest);
      }
    }
  }

  private publish(cardId: string, entry: CardLiveStateCacheEntry | undefined): void {
    this.snapshots.set(cardId, {
      entry,
      consecutiveErrors: this.getConsecutiveErrors(cardId),
    });
    this.trimUnobservedSnapshots();
    this.listeners.get(cardId)?.forEach((listener) => listener());
  }

  private trimUnobservedSnapshots(): void {
    while (this.snapshots.size > this.maxEntries) {
      let removed = false;
      for (const key of this.snapshots.keys()) {
        if (this.listeners.has(key)) continue;
        this.snapshots.delete(key);
        removed = true;
        break;
      }
      // More than maxEntries may be actively observed; ref-count cleanup owns those.
      if (!removed) return;
    }
  }
}

/** Process-local transport cache. TanStack Query owns request observation/GC. */
export const cardLiveStateTransport = new CardLiveStateTransport();
